import React, { useRef, useEffect, useState } from 'react';

/**
 * Scroll-triggered reveal wrapper using IntersectionObserver.
 * Children fade + slide / scale into view exactly once, when they
 * first enter the viewport. Multiple `variant`s for visual variety.
 *
 * Usage:
 *   <Reveal variant="up" delay={120}><Card /></Reveal>
 *   <Reveal variant="left"> ... </Reveal>
 */
const VARIANTS = {
  up:    { hidden: 'opacity-0 translate-y-10',  shown: 'opacity-100 translate-y-0' },
  down:  { hidden: 'opacity-0 -translate-y-10', shown: 'opacity-100 translate-y-0' },
  left:  { hidden: 'opacity-0 -translate-x-10', shown: 'opacity-100 translate-x-0' },
  right: { hidden: 'opacity-0 translate-x-10',  shown: 'opacity-100 translate-x-0' },
  scale: { hidden: 'opacity-0 scale-90',        shown: 'opacity-100 scale-100' },
  fade:  { hidden: 'opacity-0',                 shown: 'opacity-100' },
};

const Reveal = ({
  children,
  variant = 'up',
  delay = 0,
  duration = 700,
  className = '',
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
  as: Tag = 'div',
}) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Fallback: if IO is unsupported, just show.
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }

    // Respect users' reduced-motion preference
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setSeen(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSeen(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin]);

  const v = VARIANTS[variant] || VARIANTS.up;

  return (
    <Tag
      ref={ref}
      className={`will-change-transform transition-all ease-out ${seen ? v.shown : v.hidden} ${className}`}
      style={{
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
